/**
 * ThreadView — right panel showing the full message thread.
 *
 * Features:
 *   - Original message + all replies in chronological order
 *   - Each message/reply shows sender, timestamp, decrypted body
 *   - Attachment list with download button
 *   - Reply textarea at the bottom
 *   - "Generate PDF" button → calls /api/pdf/generate
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  messages as messagesApi,
  MessageDetail,
  AttachmentMeta,
  pdf as pdfApi,
  ghlContactUrl,
  validateAttachment,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
} from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';
import MessageBody from '../../../components/MessageBody.js';

interface Props {
  messageId: string;
  /** Star state of this thread when opened (from the inbox row). Lets the header
   *  star render filled/empty without an extra fetch. */
  initialStarred?: boolean;
  /** Called after the thread's staff read-state changes (open / reply / manual
   *  toggle) so the parent can refresh the inbox unread dot. */
  onChanged?: () => void;
  /** Called after the thread is trashed so the parent can close it + refresh. */
  onTrashed?: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ThreadView({ messageId, initialStarred = false, onChanged, onTrashed }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [detail,       setDetail]     = useState<MessageDetail | null>(null);
  const [loading,      setLoading]    = useState(true);
  const [error,        setError]      = useState<string | null>(null);
  const [replyBody,    setReplyBody]  = useState('');
  const [replying,     setReplying]   = useState(false);
  const [replyError,   setReplyError] = useState<string | null>(null);
  const [replyFiles,   setReplyFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pdfLoading,   setPdfLoading] = useState(false);
  const [pdfError,     setPdfError]   = useState<string | null>(null);
  const [marking,      setMarking]    = useState(false);
  const [starred,      setStarred]    = useState(initialStarred);
  const [statusBusy,   setStatusBusy] = useState(false);
  // Staff-side read state for THIS thread. Opening a thread marks it read on the
  // server (read.ts), so it starts read; the toggle can flip it back to unread.
  const [unread,       setUnread]     = useState(false);

  // silent=true refreshes in the background (live-update poll) without the
  // "Loading…" flash replacing the visible thread.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await messagesApi.read(messageId);
      if (res.success && res.data) {
        setDetail(res.data.message);
        setUnread(false);   // the read above marked the thread read for staff
        onChanged?.();      // refresh the inbox so the dot clears
      } else if (!silent) {
        setError(res.error ?? 'Failed to load message');
      }
    } catch {
      if (!silent) setError('Failed to load message');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [messageId, onChanged]);

  const toggleRead = async () => {
    setMarking(true);
    const targetRead = unread; // currently unread → mark read; currently read → mark unread
    try {
      const res = await messagesApi.setReadStatus(messageId, targetRead);
      if (res.success) {
        setUnread(!targetRead);
        onChanged?.();
      }
    } finally {
      setMarking(false);
    }
  };

  const toggleStar = async () => {
    const next = !starred;
    setStarred(next); // optimistic
    setStatusBusy(true);
    try {
      const res = await messagesApi.setStatus(messageId, { starred: next });
      if (res.success) onChanged?.();
      else setStarred(!next); // revert on failure
    } catch {
      setStarred(!next);
    } finally {
      setStatusBusy(false);
    }
  };

  const trashThread = async () => {
    setStatusBusy(true);
    try {
      const res = await messagesApi.setStatus(messageId, { deleted: true });
      if (res.success) onTrashed?.();
    } finally {
      setStatusBusy(false);
    }
  };

  useEffect(() => { void load(); }, [load]);

  // Live updates: poll the cheap meta probe (no PHI, no audit) and re-fetch the
  // full thread only when the reply count actually changed. Paused while the
  // tab is hidden. The replyCount ref avoids re-creating the interval per load.
  const replyCountRef = useRef<number | null>(null);
  useEffect(() => {
    replyCountRef.current = detail ? detail.replies.length : null;
  }, [detail]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (document.hidden || replyCountRef.current === null) return;
      try {
        const res = await messagesApi.meta(messageId);
        if (res.success && res.data && res.data.replyCount !== replyCountRef.current) {
          await load({ silent: true });
        }
      } catch { /* transient poll failure — next tick retries */ }
    }, 15_000);
    return () => clearInterval(timer);
  }, [messageId, load]);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-picking same file
    if (!picked.length) return;
    const errors: string[] = [];
    const valid: File[] = [];
    for (const f of picked) {
      const err = validateAttachment(f);
      if (err) errors.push(err); else valid.push(f);
    }
    let combined = [...replyFiles, ...valid];
    if (combined.length > ATTACHMENT_MAX_COUNT) {
      errors.push(`You can attach at most ${ATTACHMENT_MAX_COUNT} files per reply.`);
      combined = combined.slice(0, ATTACHMENT_MAX_COUNT);
    }
    setReplyFiles(combined);
    setReplyError(errors.length ? errors.join(' ') : null);
  };

  const removeFile = (idx: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReply = async () => {
    if (!replyBody.trim()) { setReplyError('Reply cannot be empty'); return; }
    setReplying(true);
    setReplyError(null);
    try {
      const res = await messagesApi.reply(messageId, replyBody.trim());
      if (!res.success || !res.data) {
        setReplyError(res.error ?? 'Failed to send reply');
        return;
      }
      // Two-step (non-atomic): upload each file against the new reply. A file
      // failure doesn't undo the sent reply — surface which ones to retry.
      const replyId = res.data.replyId;
      const failed: string[] = [];
      for (const f of replyFiles) {
        const up = await messagesApi.attach(messageId, f, replyId);
        if (!up.success) failed.push(f.name);
      }
      setReplyBody('');
      setReplyFiles([]);
      await load(); // Refresh thread
      if (failed.length) {
        setReplyError(`Reply sent, but ${failed.length} file(s) failed to upload: ${failed.join(', ')}. Please attach them again.`);
      }
    } catch {
      setReplyError('Unexpected error sending reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDownload = async (attachmentId: string) => {
    const res = await messagesApi.downloadUrl(messageId, attachmentId);
    if (res.success && res.data) {
      window.open(res.data.url, '_blank');
    }
  };

  const handleGeneratePdf = async (force = false) => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      const res = await pdfApi.generate({ type: 'message_thread', entityId: messageId, force });
      if (res.success && res.data) {
        window.open(res.data.url, '_blank');
      } else {
        setPdfError(res.error ?? 'PDF generation failed');
      }
    } catch {
      setPdfError('PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.center}><p style={styles.muted}>Loading message…</p></div>;
  }

  if (error || !detail) {
    return <div style={styles.center}><p style={{ color: '#c0392b' }}>{error ?? 'Message not found'}</p></div>;
  }

  const allEntries = [
    { id: detail.id, fromType: detail.fromType, fromId: detail.fromId, body: detail.body, createdAt: detail.createdAt, attachments: detail.attachments, isOriginal: true },
    ...detail.replies.map((r) => ({ ...r, isOriginal: false })),
  ];

  const renderAttachments = (list: AttachmentMeta[]) =>
    list.length > 0 ? (
      <div style={styles.bubbleAttachments}>
        {list.map((a) => (
          <button
            key={a.id}
            style={styles.attachmentChip}
            onClick={() => handleDownload(a.id)}
            title={`Download ${a.filename}`}
          >
            📎 {a.filename}{' '}
            <span style={styles.attachmentSize}>({(a.sizeBytes / 1024).toFixed(0)} KB)</span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.subject}>{detail.subject}</h2>
          <p style={styles.headerMeta}>
            {'To: '}
            <a
              href={ghlContactUrl(locationId, detail.toContactGhlId)}
              target="_blank"
              rel="noreferrer"
              style={styles.contactLink}
            >
              {detail.toContactName || detail.toContactGhlId}
            </a>
            {' · '}{formatDate(detail.createdAt)}
            {detail.readAt && <span style={styles.readBadge}> · Read {formatDate(detail.readAt)}</span>}
          </p>
        </div>
        <div style={styles.headerActions}>
          <button
            style={{ ...styles.markBtn, ...(starred ? styles.markBtnStar : {}) }}
            onClick={toggleStar}
            disabled={statusBusy}
            title={starred ? 'Unstar' : 'Star'}
            aria-label={starred ? 'Unstar' : 'Star'}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
          <button
            style={{ ...styles.markBtn, ...(unread ? styles.markBtnUnread : {}) }}
            onClick={toggleRead}
            disabled={marking}
            title={unread ? 'Mark as Read' : 'Mark as Unread'}
            aria-label={unread ? 'Mark as Read' : 'Mark as Unread'}
          >
            {marking ? '…' : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            )}
          </button>
          <button
            style={styles.markBtn}
            onClick={trashThread}
            disabled={statusBusy}
            title="Trash"
            aria-label="Trash"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" x2="10" y1="11" y2="17" />
              <line x1="14" x2="14" y1="11" y2="17" />
            </svg>
          </button>
          <button
            style={styles.pdfBtn}
            onClick={() => handleGeneratePdf(false)}
            disabled={pdfLoading}
            title="Generate PDF of this thread"
          >
            {pdfLoading ? 'Generating…' : '📄 PDF'}
          </button>
          <button
            style={styles.pdfBtn}
            onClick={() => handleGeneratePdf(true)}
            disabled={pdfLoading}
            title="Regenerate PDF (ignore cached copy)"
            aria-label="Regenerate PDF"
          >
            ↻
          </button>
        </div>
      </div>
      {pdfError && <div style={styles.errorBanner}>{pdfError}</div>}

      {/* Thread */}
      <div style={styles.thread}>
        {allEntries.map((entry) => {
          const isStaff = entry.fromType === 'staff';
          return (
            <div
              key={entry.id}
              style={{ ...styles.bubble, ...(isStaff ? styles.bubbleStaff : styles.bubbleContact) }}
            >
              <div style={styles.bubbleHeader}>
                <span style={styles.sender}>{isStaff ? `🏥 ${detail.fromName || 'Staff'}` : `👤 ${detail.toContactName || 'Patient'}`}</span>
                <span style={styles.bubbleTime}>{formatDate(entry.createdAt)}</span>
              </div>
              <MessageBody body={entry.body} style={styles.bubbleBody} />
              {renderAttachments(entry.attachments)}
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      <div style={styles.replyBox}>
        {replyError && <div style={styles.errorBanner}>{replyError}</div>}
        <textarea
          style={styles.replyTextarea}
          placeholder="Write a reply…"
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          disabled={replying}
          rows={4}
        />
        {replyFiles.length > 0 && (
          <div style={styles.fileChips}>
            {replyFiles.map((f, i) => (
              <span key={`${f.name}-${i}`} style={styles.fileChip}>
                📎 {f.name}
                <button
                  style={styles.fileChipRemove}
                  onClick={() => removeFile(i)}
                  disabled={replying}
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />
        <div style={styles.replyFooter}>
          <button
            style={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={replying}
            title="Attach files (PDF, JPG, PNG, HEIC, DOCX · max 25 MB)"
          >
            📎 Attach
          </button>
          <button
            style={{ ...styles.primary, opacity: replying ? 0.6 : 1 }}
            onClick={handleReply}
            disabled={replying}
          >
            {replying ? 'Sending…' : '🔒 Send Reply'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    background:    '#fff',
  },
  center: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100%',
  },
  muted: { color: '#9ca3af', fontSize: 14 },
  header: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'flex-start',
    padding:        '16px 20px',
    borderBottom:   '1px solid #e5e7eb',
  },
  subject: { margin: 0, fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 4 },
  headerMeta: { margin: 0, fontSize: 13, color: '#6b7280' },
  readBadge:   { color: '#059669' },
  contactLink: { color: '#2563eb', textDecoration: 'underline' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  pdfBtn: {
    background: '#f3f4f6', border: '1px solid #d1d5db',
    borderRadius: 6, padding: '6px 12px',
    cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
  },
  markBtn: {
    background: '#f3f4f6', border: '1px solid #d1d5db',
    borderRadius: 6, padding: '6px 10px',
    cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', color: '#374151',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  markBtnUnread: {
    background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb',
  },
  markBtnStar: {
    background: '#fffbeb', borderColor: '#fde68a', color: '#f59e0b',
  },
  thread: {
    flex:      1,
    overflowY: 'auto',
    padding:   '16px 20px',
    display:   'flex',
    flexDirection: 'column',
    gap:       12,
  },
  bubble: {
    borderRadius: 10,
    padding:      '12px 14px',
    maxWidth:     '80%',
  },
  bubbleStaff: {
    background:  '#eff6ff',
    border:      '1px solid #bfdbfe',
    alignSelf:   'flex-end',
  },
  bubbleContact: {
    background: '#f9fafb',
    border:     '1px solid #e5e7eb',
    alignSelf:  'flex-start',
  },
  bubbleHeader: {
    display:        'flex',
    justifyContent: 'space-between',
    marginBottom:   6,
  },
  sender:     { fontSize: 12, fontWeight: 600, color: '#374151' },
  bubbleTime: { fontSize: 11, color: '#9ca3af' },
  bubbleBody: { margin: 0, fontSize: 14, color: '#111827', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  attachmentBlock: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: 12,
    marginTop:  4,
  },
  attachmentTitle: { margin: '0 0 8px 0', fontSize: 13, fontWeight: 600, color: '#374151' },
  attachmentRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    marginBottom: 6,
    fontSize:   13,
  },
  attachmentName: { color: '#2563eb', flex: 1 },
  attachmentSize: { color: '#9ca3af' },
  downloadBtn: {
    background: 'none', border: '1px solid #d1d5db',
    borderRadius: 4, padding: '2px 10px',
    cursor: 'pointer', fontSize: 12, color: '#374151',
  },
  replyBox: {
    borderTop: '1px solid #e5e7eb',
    padding:   '12px 20px',
    flexShrink: 0,
  },
  replyTextarea: {
    width:  '100%',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 14,
    color: '#111827',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  replyFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  primary: {
    background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 6, padding: '8px 20px', cursor: 'pointer',
    fontSize: 14, fontWeight: 500,
  },
  attachBtn: {
    background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
    padding: '8px 14px', cursor: 'pointer', fontSize: 14, color: '#374151',
  },
  bubbleAttachments: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  attachmentChip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
    padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#2563eb',
    maxWidth: '100%', textAlign: 'left',
  },
  fileChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  fileChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
    padding: '3px 8px', fontSize: 12, color: '#1e40af',
  },
  fileChipRemove: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#1e40af', fontSize: 15, lineHeight: 1, padding: 0,
  },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '8px 12px',
    color: '#b91c1c', fontSize: 13, marginBottom: 8,
  },
};
